import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { getAuthHeaders } from '../../../contexts/AuthContext';
import { API } from '../execShared';

export default function ExecCalendarSection() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date());

  useEffect(() => {
    axios.get(`${API}/schedule/executive`, { headers: getAuthHeaders() })
      .then(r => setSchedules(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const year = month.getFullYear();
  const mo = month.getMonth();
  const firstDay = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const monthName = month.toLocaleString('default', { month: 'long', year: 'numeric' });

  const byDate = {};
  schedules.forEach(s => { if (s.scheduled_date) { if (!byDate[s.scheduled_date]) byDate[s.scheduled_date] = []; byDate[s.scheduled_date].push(s); } });

  const getStatus = (dateStr) => {
    const items = byDate[dateStr];
    if (!items) return null;
    if (items.some(i => i.missed)) return 'missed';
    return 'scheduled';
  };

  const dots = { scheduled: 'bg-[#FF4343]', missed: 'bg-[#6B7280]' };

  if (loading) return <p className="text-[#9CA3AF]">Loading calendar...</p>;

  return (
    <div className="space-y-6" data-testid="exec-calendar-section">
      <h2 className="font-display text-2xl font-bold">My <span className="text-[#FF4343]">Calendar</span></h2>

      {/* Upcoming posts */}
      {schedules.length > 0 && (
        <div className="space-y-2 mb-6">
          <h3 className="text-xs font-mono uppercase text-[#6B7280] mb-2">Upcoming Posts</h3>
          {schedules.filter(s => !s.missed && s.scheduled_date >= new Date().toISOString().split('T')[0]).slice(0, 5).map(s => (
            <div key={s.id} className="bg-[#111827] border border-white/10 p-4 flex items-center justify-between border-l-[3px] border-l-[#FF4343]">
              <div>
                <span className="text-sm text-white font-medium">{s.campaign_title || 'Campaign'}</span>
                <div className="flex items-center gap-2 mt-1">
                  <CalendarDays className="w-3 h-3 text-[#6B7280]" />
                  <span className="text-xs text-[#9CA3AF]">{new Date(s.scheduled_date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                </div>
              </div>
              <Link to="/dashboard" onClick={() => {}} className="text-[10px] px-3 py-1.5 border border-[#FF4343] text-[#FF4343] hover:bg-[#FF4343] hover:text-[#090E33] transition-colors">
                Submit Post
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Month Grid */}
      <div className="bg-[#111827] border border-white/10 p-5">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setMonth(new Date(year, mo - 1, 1))} className="text-[#6B7280] hover:text-white p-1"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-semibold">{monthName}</span>
          <button onClick={() => setMonth(new Date(year, mo + 1, 1))} className="text-[#6B7280] hover:text-white p-1"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-px mb-1">
          {['S','M','T','W','T','F','S'].map((d, i) => (
            <div key={i} className="text-center text-[9px] font-mono text-[#6B7280] uppercase py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px" data-testid="exec-calendar-grid">
          {Array(firstDay).fill(null).map((_, i) => <div key={`e-${i}`} className="h-12 bg-[#090E33]/30" />)}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dateStr = `${year}-${String(mo + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const status = getStatus(dateStr);
            const isToday = dateStr === new Date().toISOString().split('T')[0];
            const items = byDate[dateStr] || [];

            return (
              <div key={day} className={`h-12 bg-[#090E33] border ${isToday ? 'border-[#FF4343]/40' : 'border-white/5'} relative p-1 group`}>
                <span className={`text-[10px] ${isToday ? 'text-[#FF4343] font-bold' : 'text-[#6B7280]'}`}>{day}</span>
                {status && (
                  <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${dots[status]}`} />
                )}
                {items.length > 0 && (
                  <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10 pointer-events-none">
                    <span className="text-[8px] text-white text-center px-1">{items[0].campaign_title?.slice(0, 20)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-3 text-[9px] text-[#6B7280]">
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#FF4343]" /> Post Due</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#6B7280]" /> Missed</span>
        </div>
      </div>

      {/* Missed Posts */}
      {schedules.filter(s => s.missed).length > 0 && (
        <div>
          <h3 className="text-xs font-mono uppercase text-[#FF4343] mb-2">Missed Deadlines</h3>
          {schedules.filter(s => s.missed).map(s => (
            <div key={s.id} className="bg-[#111827] border border-[#FF4343]/20 p-3 mb-1.5 flex items-center justify-between">
              <div>
                <span className="text-xs text-white">{s.campaign_title}</span>
                <span className="text-[10px] text-[#FF4343] ml-2">Due: {s.scheduled_date}</span>
              </div>
              <span className="text-[9px] font-mono text-[#FF4343] bg-[#FF4343]/10 px-2 py-0.5">MISSED</span>
            </div>
          ))}
        </div>
      )}

      {schedules.length === 0 && (
        <div className="text-center py-12 bg-[#111827] border border-white/10">
          <CalendarDays className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-[#9CA3AF] text-sm">No posts scheduled yet.</p>
          <p className="text-[10px] text-[#6B7280] mt-1">When you accept a campaign, you'll schedule your post dates here.</p>
        </div>
      )}
    </div>
  );
}


